import React, {useCallback, useMemo, useState} from 'react';
import {Controller} from 'react-hook-form';
import {
    FormError,
    FormLabel,
    getInputClassNames,
    getInputWrapperClass,
    getWholeDivWrapperClass,
    NUMBER_OVERLAY_RIGHT_CLASS,
    NUMBER_OVERLAY_LEFT_CLASS,
    SUFFIX_CLASS,
} from './sharedStyles.tsx';
import {FormatNumberAs, FormComponentProps, NumberComponent, ParentController} from "../DynamicFormTypes.ts";
import {ValidationFactory} from "../ValidationFactory.ts";
import {useFormMethods} from "../DyanmicFormContext.tsx";
import {useFieldBlurHandler} from "../hooks/OnFieldBlurHanlder.tsx";
import {useValidationRules} from "../hooks/useValidationRules.tsx";


type NumberFormatHandler = {
    renderOverlay?: () => React.ReactNode;
    normalizeOnBlur?: (value: unknown) => number | '';
    step?: string;
    isCurrency?: boolean;
    currencyDecimals?: 0 | 2;
};

const toNumberOrEmpty = (value: unknown): number | '' => {
    if (value === '' || value === undefined || value === null) return '';
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(n) ? '' : n;
};


const roundTo = (n: number, decimals: number) => {
    const factor = Math.pow(10, decimals);
    return Math.round(n * factor) / factor;
};


const numberFormatHandlers: Record<FormatNumberAs, NumberFormatHandler> = {
    [FormatNumberAs.NONE]: {
        step: 'any',
        isCurrency: false,
    },
    [FormatNumberAs.PERCENT]: {
        renderOverlay: () => (
            <span className={NUMBER_OVERLAY_RIGHT_CLASS}>%</span>
        ),
        step: 'any',
        isCurrency: false,
    },
    [FormatNumberAs.INTEGER]: {
        step: '1',
        normalizeOnBlur: (value: unknown) => {
            const num = toNumberOrEmpty(value);
            if (num === '') return '';
            return Math.trunc(num);
        },
        isCurrency: false,
    },
    [FormatNumberAs.DOUBLE]: {
        step: '0.01',
        normalizeOnBlur: (v) => {
            const n = toNumberOrEmpty(v);
            if (n === '') return '';
            return roundTo(n, 2);
        },
        isCurrency: false,
    },
    [FormatNumberAs.CURRENCY_DOUBLE]: {
        step: '0.01',
        renderOverlay: () => <span className={NUMBER_OVERLAY_LEFT_CLASS}>$</span>,
        normalizeOnBlur: (v) => {
            const n = toNumberOrEmpty(v);
            if (n === '') return '';
            return roundTo(n, 2);
        },
        isCurrency: true,
        currencyDecimals: 2
    },
    [FormatNumberAs.CURRENCY_INTEGER]: {
        step: '1',
        renderOverlay: () => <span className={NUMBER_OVERLAY_LEFT_CLASS}>$</span>,
        normalizeOnBlur: (v) => {
            const n = toNumberOrEmpty(v);
            if (n === '') return '';
            return Math.trunc(n);
        },
        isCurrency: true,
        currencyDecimals: 0
    }
};




type NumberInputProps = {
    component: NumberComponent;
    disabled: boolean;
    isCurrency: boolean;
    currencyDecimals?: 0 | 2;
    min: number;
    max?: number;
    step: string;
    overlay: React.ReactNode;
    onBlurCombined: () => void;
    field: ParentController['field'];
    fieldState: ParentController['fieldState'];
};
const NumberInput = React.memo(function NumberInput({
                                                        component,
                                                        disabled,
                                                        isCurrency = false,
                                                        currencyDecimals,
                                                        min,
                                                        max,
                                                        step,
                                                        overlay,
                                                        onBlurCombined,
                                                        field,
                                                        fieldState,
                                                    }: NumberInputProps) {
    const [isFocused, setIsFocused] = useState(false);
    const inputClassName = useMemo(() => {
        // Add left padding for currency so the $ doesn’t overlap typed text
        const currencyPadding = isCurrency ? ' pl-8' : '';
        const base = getInputClassNames(component, fieldState, disabled) + ' flex-grow' + currencyPadding;
        return disabled ? `${base} bg-gray-50` : base;
    }, [component, fieldState, disabled, isCurrency]);

    const currencyFormatter = useMemo(() => {
        if (!isCurrency) return null;
        const decimals = currencyDecimals ?? 2;
        return new Intl.NumberFormat(undefined, {
            useGrouping: true,
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    }, [isCurrency, currencyDecimals]);

    const displayValue = useMemo(() => {
        const raw = field.value;
        if (!isCurrency) return raw ?? '';

        if (raw === '' || raw === undefined || raw === null) return '';

        // While focused: show the raw string value to preserve partial input
        if (isFocused) {
            return typeof raw === 'string' ? raw : String(raw);
        }

        // While blurred: parse to number and format with currency formatting
        const numValue = typeof raw === 'string' ? Number(raw) : raw;
        if (Number.isNaN(numValue)) return '';

        return currencyFormatter ? currencyFormatter.format(numValue) : String(numValue);
    }, [field.value, isCurrency, isFocused, currencyFormatter]);

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const text = e.target.value;

            if (text.trim() === '') {
                field.onChange('');
                return;
            }

            // Remove formatting characters, keep digits, minus, and decimal
            const cleaned = text.replace(/[^\d.-]/g, '');

            // Reject invalid inputs: multiple decimals or multiple negative signs
            const decimalCount = (cleaned.match(/\./g) || []).length;
            const negativeCount = (cleaned.match(/-/g) || []).length;

            if (decimalCount > 1 || negativeCount > 1) {
                field.onChange('');
                return;
            }

            // Always store the cleaned string as-is while typing
            // This preserves partial inputs like "5.", "0.", ".5", etc.
            field.onChange(cleaned);
        },
        [field, isCurrency]
    );

    const onBlur = useCallback(() => {
        setIsFocused(false);
        onBlurCombined();
    }, [onBlurCombined]);

    const onFocus = useCallback(() => {
        setIsFocused(true);
    }, []);


    return (
        <div className={getWholeDivWrapperClass(component)}>
            <FormLabel component={component} />

            <div className={getInputWrapperClass(component) + ' flex relative'}>
                <input
                    id={component.fieldKey}
                    type={'text'}
                    inputMode={isCurrency ? 'decimal' : 'numeric'}
                    min={isCurrency ? undefined : min}
                    max={isCurrency ? undefined : max}
                    step={isCurrency ? undefined : step}
                    className={inputClassName}
                    name={field.name}
                    ref={field.ref}
                    value={displayValue}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={disabled}
                />

                {overlay}

                {component.suffix && <span className={`${SUFFIX_CLASS} text-xs`}>{component.suffix}</span>}
            </div>

            <FormError fieldState={fieldState} component={component} />
        </div>
    );
});

interface NumberComponentProps extends FormComponentProps<NumberComponent> {
    disabled?: boolean;
    parentController?: ParentController
}

export const NumberRenderer: React.FC<NumberComponentProps> =
    ({ component, disabled = false, parentController = undefined }) => {

        const { formMethods } = useFormMethods()
        const formatType = component.formatNumberAs || FormatNumberAs.NONE;
        const handler = numberFormatHandlers[formatType] ?? numberFormatHandlers[FormatNumberAs.NONE];

        const min = useMemo(() => ValidationFactory.getMinValue(component, 0), [component]);
        const max = useMemo(() => ValidationFactory.getMaxValue(component, undefined), [component]);
        const validate = useValidationRules({ component });
        const step = handler.step || 'any';
        const overlay = useMemo(() => handler.renderOverlay?.() ?? null, [handler]);
        const isCurrency = handler.isCurrency || false;

        const { handleFieldBlur } = useFieldBlurHandler({
            component
        });

        const makeOnBlurCombined = useCallback(
            (fieldOnBlur: () => void, fieldOnChange: (v: unknown) => void, currentValue: unknown) => () => {
                // First, convert any string value to a number before normalizing
                let valueToNormalize = currentValue;
                if (isCurrency && typeof currentValue === 'string') {
                    const parsed = Number(currentValue);
                    valueToNormalize = Number.isNaN(parsed) ? '' : parsed;
                }

                fieldOnBlur();

                if (handler.normalizeOnBlur) {
                    const normalized = handler.normalizeOnBlur(valueToNormalize);
                    if (normalized !== valueToNormalize) {
                        fieldOnChange(normalized);
                    }
                } else if (valueToNormalize !== currentValue) {
                    // Even without normalizeOnBlur, convert string to number on blur
                    fieldOnChange(valueToNormalize);
                }

                handleFieldBlur();
            },
            [handler, handleFieldBlur, isCurrency]
        );


        const renderInput = useCallback(
            ({ field, fieldState }: ParentController) => {
                const onBlurCombined = makeOnBlurCombined(field.onBlur, field.onChange, field.value);

                return (
                    <NumberInput
                        component={component}
                        disabled={disabled}
                        min={min}
                        max={max}
                        step={step}
                        overlay={overlay}
                        field={field}
                        isCurrency={isCurrency}
                        currencyDecimals={handler.currencyDecimals}
                        fieldState={fieldState}
                        onBlurCombined={onBlurCombined}
                    />
                );
            },
            [component, disabled, min, max, step, overlay, makeOnBlurCombined, isCurrency, handler.currencyDecimals]
        );


        if (parentController) {
            return renderInput(parentController);
        }

        return (
            <Controller
                name={component.fieldKey}
                control={formMethods.control}
                rules={validate}
                render={renderInput}
            />
        );
    };


export const NumberField = React.memo(NumberRenderer);